import { useMutation, useQuery } from '@tanstack/react-query'
import { httpClient, ROOT_URL } from '../App'
import { BaseKey } from 'node_modules/@refinedev/core/dist'
import { AxiosResponse } from 'axios'

export interface IWalletBody {
  name?: string
  currencyId?: number
  assetConfigGroupId?: number | string
  address?: string
  companyId?: number
  userId?: number | null
  networkId?: number | null
}

export async function handleConnect() {
  const wallets = await window.ethereum.request({
    method: 'eth_requestAccounts',
  })

  if (wallets.length > 0) {
    await httpClient.post(`${ROOT_URL}/api/v1/blockchain/wallet`, {
      wallet: wallets[0],
    })
    return wallets[0]
  }
}

export const useGetListCryptoWithdrawal = (params: {
  start: number
  end: number
  name?: string
  status?: string
  gte?: string
  lte?: string
  timeZone?: string
}) => {
  return useQuery<{
    data: {
      data: {
        items: []
        total: number
        page: number
        pageSize: number
        totalPages: number
      }
    }
  }>(
    [
      `/api/v1/withdrawal-requests`,
      params.start,
      params.end,
      params.name,
      params.status,
      params.gte,
      params.lte,
      params.timeZone,
    ],
    async () => getListCryptoWithdrawal(params)
  )
}

export function getListCryptoWithdrawal({
  start,
  end,
  name,
  status,
  gte,
  lte,
  timeZone,
}: {
  start: number
  end: number
  name?: string
  status?: string
  gte?: string
  lte?: string
  timeZone?: string
}) {
  const params = new URLSearchParams()
  params.append('_start', String(start))
  params.append('_end', String(end))
  if (name) params.append('_like', name)
  if (status) params.append('_status', status)
  if (gte) params.append('_gte', gte)
  if (lte) params.append('_lte', lte)
  if (timeZone) params.append('_timeZone', timeZone)
  window.dispatchEvent(new CustomEvent('updateUnreadCounts'))
  return httpClient.get(
    `${ROOT_URL}/api/v1/withdrawal-requests?${params.toString()}`
  )
}

export const useGetDetailWithdrawal = (id: BaseKey) => {
  return useQuery<{ data: any }>(
    [`/api/v1/withdrawal-requests/:id`, id],
    async () => getDetailWithdrawal(id),
    { enabled: !!id }
  )
}

export function getDetailWithdrawal(id: BaseKey) {
  return httpClient.get(`${ROOT_URL}/api/v1/withdrawal-requests/${id}`)
}

export const useChangeStatusWithdrawal = (config = {}) => {
  return useMutation({
    ...config,
    mutationFn: changeStatusWithdrawal,
  })
}

export function changeStatusWithdrawal(params: {
  id: BaseKey
  status: string
}) {
  const body = {
    status: params.status,
  }
  return httpClient.put(
    `${ROOT_URL}/api/v1/withdrawal-requests/update/${params.id}`,
    body
  )
}

export const useCreateWallet = (config = {}) => {
  return useMutation({
    ...config,
    mutationFn: createWallet,
  })
}

export function createWallet(body: IWalletBody): Promise<AxiosResponse<any>> {
  return httpClient.post(`${ROOT_URL}/api/v1/company-wallets/create`, body)
}

export const useGetListWallet = (params: {
  companyId: number
  start: number
  end: number
  name?: string
  currencyId?: number
  enabled?: boolean
}) => {
  const { enabled = true, ...queryParams } = params;
  return useQuery<{
    data: {
      data: {
        total: number
        page: number
        pageSize: number
        totalPages: number
        items: []
      }
    }
  }>(
    [
      `/api/v1/wallets/company-wallets/get-list`,
      queryParams.companyId,
      queryParams.start,
      queryParams.end,
      queryParams.name,
      queryParams.currencyId,
    ],
    async () => getListWallet(queryParams),
    {
      enabled: enabled && !!queryParams.companyId,
    }
  )
}

export const useGetListWalletProvider = (params: {
  companyId: number
  start: number
  end: number
  name?: string
  currencyId?: number
}) => {
  return useQuery<{
    data: {
      data: {
        total: number
        page: number
        pageSize: number
        totalPages: number
        items: []
      }
    }
  }>(
    [
      `/api/v1/wallets/company-wallets/get-list/provider`,
      params.companyId,
      params.start,
      params.end,
      params.name,
      params.currencyId,
    ],
    async () => getListWallet(params),
    {
      enabled: !!params.companyId,
    }
  )
}

export function getListWallet({
  companyId,
  start,
  end,
  name,
  currencyId,
}: {
  companyId: number
  start: number
  end: number
  name?: string
  currencyId?: number
}) {
  const params = new URLSearchParams()
  params.append('_start', String(start))
  params.append('_end', String(end))
  if (name) params.append('_like', name)
  if (currencyId) params.append('_currencyId', String(currencyId))
  if (companyId) params.append('_companyId', String(companyId))
  return httpClient.get(
    `${ROOT_URL}/api/v1/company-wallets/get-list?${params.toString()}`
  )
}

export const useGetListProviderWallet = (params: {
  start: number
  end: number
  name?: string
  status?: string
}) => {
  return useQuery<{
    data: {
      data: {
        items: []
        total: number
        page: number
        pageSize: number
        totalPages: number
      }
    }
  }>(
    [
      `/api/v1/company-wallets/get-list`,
      params.start,
      params.end,
      params.name,
      params.status,
    ],
    async () => getListProviderWallet(params)
  )
}

export function getListProviderWallet({
  start,
  end,
  name,
  status,
}: {
  start: number
  end: number
  name?: string
  status?: string
}) {
  const params = new URLSearchParams()
  params.append('_start', String(start))
  params.append('_end', String(end))
  if (name) params.append('_like', name)
  if (status) params.append('_status', status)
  return httpClient.get(
    `${ROOT_URL}/api/v1/company-wallets/get-list?${params.toString()}`
  )
}

export const useGetDetailWallet = (id: number) => {
  return useQuery<{ data: { data: any } }>(
    [`/api/v1/wallets/:id`, id],
    async () => getDetailWallet(id),
    {
      enabled: !!id,
    }
  )
}

export function getDetailWallet(id: number | null) {
  return httpClient.get(`${ROOT_URL}/api/v1/company-wallets/${id}`)
}

export const useUpdateWallet = (config = {}) => {
  return useMutation({
    ...config,
    mutationFn: updateWallet,
  })
}

export function updateWallet(
  body: IWalletBody & { id: number }
): Promise<AxiosResponse<any>> {
  return httpClient.patch(`${ROOT_URL}/api/v1/company-wallets/update`, body)
}

export const useDeleteWallet = (config = {}) => {
  return useMutation({
    ...config,
    mutationFn: deleteWallet,
  })
}

export function deleteWallet(id: number | null) {
  return httpClient.delete(`${ROOT_URL}/api/v1/company-wallets/${id}`)
}

export const useGetListExchange = (params: {
  start: number
  end: number
  name?: string
  status?: string
  gte?: string
  lte?: string
  timeZone?: string
}) => {
  return useQuery<{
    data: {
      data: {
        items: []
        total: number
        page: number
        pageSize: number
        totalPages: number
      }
    }
  }>(
    [
      `/api/v1/exchange_money_requests`,
      params.start,
      params.end,
      params.name,
      params.status,
      params.gte,
      params.lte,
      params.timeZone,
    ],
    async () => getListExchange(params)
  )
}

export function getListExchange({
  start,
  end,
  name,
  status,
  gte,
  lte,
  timeZone,
}: {
  start: number
  end: number
  name?: string
  status?: string
  gte?: string
  lte?: string
  timeZone?: string
}) {
  const params = new URLSearchParams()
  params.append('_start', String(start))
  params.append('_end', String(end))
  if (name) params.append('_like', name)
  if (status) params.append('_status', status)
  if (gte) params.append('_gte', gte)
  if (lte) params.append('_lte', lte)
  if (timeZone) params.append('_timeZone', timeZone)
  window.dispatchEvent(new CustomEvent('updateUnreadCounts'))
  return httpClient.get(
    `${ROOT_URL}/api/v1/exchange_money_requests/get-list?${params.toString()}`
  )
}

export const useGetDetailExchange = (id: BaseKey) => {
  return useQuery<{ data: any }>(
    [`/api/v1/exchange_money_requests/get-one/:id`, id],
    async () => getDetailExchange(id),
    { enabled: !!id }
  )
}

export function getDetailExchange(id: BaseKey) {
  return httpClient.get(
    `${ROOT_URL}/api/v1/exchange_money_requests/get-one/${id}`
  )
}

export const useChangeStatusExchange = (config = {}) => {
  return useMutation({
    ...config,
    mutationFn: changeStatusExchange,
  })
}

export function changeStatusExchange(params: { id: BaseKey; status: string }) {
  const body = {
    id: params.id,
    status: params.status,
  }
  return httpClient.patch(
    `${ROOT_URL}/api/v1/exchange_money_requests/update/${params.id}`,
    body
  )
}
